import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import DeliveryAddressForm, { emptyAddress } from '../Account/DeliveryAddressForm';
import './AuthModal.css';

interface AuthModalProps {
  /** Called after a successful sign-in, or when the shopper continues after signing up. */
  readonly onClose: () => void;
}

interface Credentials {
  name: string;
  email: string;
  phone: string;
  password: string;
  referral: string;
}

export default function AuthModal({ onClose }: AuthModalProps) {
  const login = useAuthStore((state) => state.login);
  const signup = useAuthStore((state) => state.signup);
  const loading = useAuthStore((state) => state.loading);
  const error = useAuthStore((state) => state.error);
  const clearError = useAuthStore((state) => state.clearError);
  const [searchParams] = useSearchParams();
  const referralFromLink = searchParams.get('ref') ?? '';
  const [tab, setTab] = useState<'login' | 'signup'>(referralFromLink ? 'signup' : 'login');
  const [identifier, setIdentifier] = useState('');
  const [form, setForm] = useState<Credentials>({
    name: '',
    email: '',
    phone: '',
    password: '',
    referral: referralFromLink,
  });
  const setField = (key: keyof Credentials) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((previous) => ({ ...previous, [key]: event.target.value }));
  const { name, email, phone, password, referral } = form;
  const [includeAddress, setIncludeAddress] = useState(false);
  const [address, setAddress] = useState(emptyAddress);
  const [signedUp, setSignedUp] = useState(false);

  const switchTab = (next: 'login' | 'signup') => {
    setTab(next);
    clearError();
  };

  const handleClose = () => {
    clearError();
    onClose();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await login(identifier, password)) {
      handleClose();
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      await signup({
        name,
        email,
        phone,
        password,
        referral_code: referral,
        ...(includeAddress ? { delivery_address: address } : {}),
      })
    ) {
      setSignedUp(true);
    }
  };

  const content = (
    <>
      {signedUp ? (
        <div className="auth-modal__welcome">
          <h2 className="auth-modal__title">Welcome, {name}!</h2>
          <p className="auth-modal__note">
            Your account is ready. We'll send a verification link to <strong>{email}</strong> — please open it
            to verify your email address.
          </p>
          <button className="auth-modal__submit" onClick={handleClose}>
            Continue shopping
          </button>
        </div>
      ) : (
        <>
          <div className="auth-modal__tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'login'}
              className={`auth-modal__tab${tab === 'login' ? ' auth-modal__tab--active' : ''}`}
              onClick={() => switchTab('login')}
            >
              Sign in
            </button>
            <button
              role="tab"
              aria-selected={tab === 'signup'}
              className={`auth-modal__tab${tab === 'signup' ? ' auth-modal__tab--active' : ''}`}
              onClick={() => switchTab('signup')}
            >
              Sign up
            </button>
          </div>

          {tab === 'login' ? (
            <form className="auth-modal__form" onSubmit={handleLogin}>
              <label className="auth-modal__label">
                Email or phone number
                <input
                  className="auth-modal__input"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </label>
              <label className="auth-modal__label">
                Password
                <input
                  className="auth-modal__input"
                  type="password"
                  value={password}
                  onChange={setField('password')}
                  required
                />
              </label>
              {error && <p className="auth-modal__error">{error}</p>}
              <button className="auth-modal__submit" type="submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          ) : (
            <form className="auth-modal__form" onSubmit={handleSignup}>
              <label className="auth-modal__label">
                Preferred name
                <input
                  className="auth-modal__input"
                  value={name}
                  onChange={setField('name')}
                  placeholder="How should we address you?"
                  required
                  autoFocus
                />
              </label>
              <label className="auth-modal__label">
                Email address
                <input
                  className="auth-modal__input"
                  type="email"
                  value={email}
                  onChange={setField('email')}
                  placeholder="you@example.com"
                  required
                />
              </label>
              <label className="auth-modal__label">
                Phone number
                <input
                  className="auth-modal__input"
                  type="tel"
                  value={phone}
                  onChange={setField('phone')}
                  placeholder="+91 98765 43210"
                  required
                />
              </label>
              <label className="auth-modal__label">
                Password
                <input
                  className="auth-modal__input"
                  type="password"
                  value={password}
                  onChange={setField('password')}
                  minLength={8}
                  required
                />
              </label>
              <label className="auth-modal__label">
                Referral code (optional)
                <input value={referral} maxLength={32} onChange={setField('referral')} />
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={includeAddress}
                  disabled={loading}
                  onChange={(event) => setIncludeAddress(event.target.checked)}
                />{' '}
                Add a delivery address
              </label>
              {includeAddress && (
                <DeliveryAddressForm value={address} disabled={loading} onChange={setAddress} />
              )}
              {error && <p className="auth-modal__error">{error}</p>}
              <button className="auth-modal__submit" type="submit" disabled={loading}>
                {loading ? 'Creating account…' : 'Sign up'}
              </button>
            </form>
          )}
        </>
      )}
    </>
  );
  return <section className="auth-page__form">{content}</section>;
}
